/**
 * Theme mode — light / dark / system.
 *
 * Stored as a string in localStorage so each admin keeps their
 * preference across sessions. The `system` value defers to the
 * browser's `prefers-color-scheme` media query, which lets users
 * inherit whatever their OS picks (and follow OS-level dark/light
 * scheduling automatically).
 *
 *   useThemeMode() → [mode, resolvedMode, setMode]
 *     mode         — what's stored: 'light' | 'dark' | 'system'
 *     resolvedMode — what should actually render: 'light' | 'dark'
 *                    (resolves 'system' through the media query)
 *     setMode(v)   — persist + broadcast
 *
 * The effective theme is applied to `<html data-theme="…">` by
 * `applyThemeAttribute(resolvedMode)` so the CSS variables in
 * themeTokens.css can switch via attribute selectors.
 *
 * Storage key namespaced as "tts.theme" (parallel to "tts.redesign.v2").
 */
import { useEffect, useState } from 'react';

const KEY = 'tts.theme';
const EVENT = 'tts:theme-changed';

const VALID = new Set(['light', 'dark', 'system']);

/* ─── Persistence + same-tab event bus ─────────────────────────────── */

export function getThemeMode() {
  try {
    const v = localStorage.getItem(KEY);
    return VALID.has(v) ? v : 'system';
  } catch (_e) {
    return 'system';
  }
}

export function setThemeMode(mode) {
  const next = VALID.has(mode) ? mode : 'system';
  try {
    localStorage.setItem(KEY, next);
    window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
  } catch (_e) { /* private browsing, etc. */ }
}

/* ─── Resolution: 'system' → the OS preference at read time ────────── */

function readSystemPreference() {
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  } catch (_e) {
    return 'light';
  }
}

export function resolveThemeMode(mode) {
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  return readSystemPreference(); // 'system' → fall through
}

/* ─── DOM application — flips <html data-theme="…"> ────────────────── */

/**
 * Apply the resolved theme to the document root. Idempotent — safe to
 * call repeatedly. Intentionally targets <html> (document.documentElement)
 * so the attribute is in scope before React even mounts; downstream
 * CSS selectors `[data-theme="dark"]` resolve without needing extra
 * context providers.
 */
export function applyThemeAttribute(resolvedMode) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolvedMode);
}

/* ─── React hook ──────────────────────────────────────────────────── */

export function useThemeMode() {
  const [mode, setMode] = useState(getThemeMode);
  const [resolved, setResolved] = useState(() => resolveThemeMode(getThemeMode()));

  // Re-read on storage events (cross-tab) and our custom event (same-tab).
  useEffect(() => {
    const sync = () => {
      const next = getThemeMode();
      setMode(next);
      setResolved(resolveThemeMode(next));
    };
    window.addEventListener('storage', sync);
    window.addEventListener(EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(EVENT, sync);
    };
  }, []);

  // When mode is 'system', track OS preference changes live too — if
  // the user has the OS scheduled to flip at sunset, we follow it
  // without a page reload.
  useEffect(() => {
    if (mode !== 'system' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setResolved(readSystemPreference());
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, [mode]);

  // Keep the DOM attribute in lockstep with `resolved`.
  useEffect(() => {
    applyThemeAttribute(resolved);
  }, [resolved]);

  return [mode, resolved, setThemeMode];
}
